import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Anchor,
    Wrench,
    Flame,
    HardHat,
    Truck,
    Sparkles,
    Hammer,
    ArrowRight,
} from "lucide-react";
import { COMPANY, IMAGES, SERVICES, STATS } from "@/lib/site";
import Marquee from "@/components/Marquee";

const SERVICE_ICONS = {
    navsea: Anchor,
    "submarine-ship": Anchor,
    pipe: Wrench,
    structural: HardHat,
    "mig-tig": Flame,
    mobile: Truck,
    fabrication: Hammer,
};

const SPAN_CLASSES = {
    navsea: "md:col-span-8 md:row-span-2",
    "submarine-ship": "md:col-span-4",
    pipe: "md:col-span-4",
    structural: "md:col-span-4",
    "mig-tig": "md:col-span-4",
    mobile: "md:col-span-4",
    fabrication: "md:col-span-4",
};

export default function HomePage() {
    return (
        <main data-testid="home-page" className="relative z-10">
            {/* HERO */}
            <section
                data-testid="hero"
                className="relative min-h-[92vh] flex items-end overflow-hidden hero-glow"
            >
                <div className="absolute inset-0">
                    <img
                        src={IMAGES.heroSparks}
                        alt="Welder at work"
                        className="w-full h-full object-cover opacity-50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-brand-bg via-brand-bg/85 to-brand-bg/30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-transparent to-transparent" />
                    <div className="absolute inset-0 stripes opacity-40" />
                </div>

                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-24 pt-32 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="flex items-center gap-3 mb-8">
                            <span className="font-mono text-xs text-brand-blue tracking-[0.3em] uppercase">
                                ◇ Naval Sea Systems Command
                            </span>
                            <span className="h-px flex-1 bg-white/20 max-w-[120px]" />
                            <span className="font-mono text-xs text-slate-500 tracking-[0.2em] uppercase">
                                Est. {new Date().getFullYear() - 4}
                            </span>
                        </div>

                        <h1 className="font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl uppercase tracking-tighter leading-[0.92]">
                            Naval-Grade
                            <br />
                            <span className="text-brand-blue">Welding.</span>
                            <br />
                            Engineered for Duty.
                        </h1>

                        <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed">
                            {COMPANY.name} deploys NAVSEA-certified welders,
                            fitters, and shipyard crews to the most demanding
                            marine and industrial programs in the United
                            States.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-quote"
                                className="group inline-flex items-center gap-3 bg-brand-blue hover:bg-[#004080] text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-7 py-4 transition-colors"
                            >
                                Request a Quote
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-cta-services"
                                className="group inline-flex items-center gap-3 border border-white/20 hover:border-white text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-7 py-4 transition-colors"
                            >
                                View Capabilities
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom stat strip */}
                <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-brand-bg/80 backdrop-blur-md">
                    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-2 md:grid-cols-4">
                        {STATS.map((s, i) => (
                            <div
                                key={s.label + i}
                                data-testid={`hero-stat-${i}`}
                                className={`py-5 ${
                                    i < 3 ? "md:border-r" : ""
                                } border-white/10 ${
                                    i < 2 ? "border-r md:border-r" : ""
                                } ${i < 2 ? "" : "border-t md:border-t-0"}`}
                            >
                                <div className="font-display font-black text-white text-xl md:text-2xl uppercase tracking-tight">
                                    {s.value}
                                </div>
                                <div className="font-mono text-[10px] tracking-[0.2em] text-slate-400 uppercase mt-1">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Marquee />

            {/* SERVICES BENTO */}
            <section
                data-testid="home-services"
                className="relative py-24 md:py-32"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
                        <div className="md:col-span-5">
                            <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                                ◇ 02 — Capabilities
                            </div>
                            <h2 className="mt-4 font-display font-black text-white text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                                Built for the
                                <br />
                                Hardest Steel.
                            </h2>
                        </div>
                        <div className="md:col-span-6 md:col-start-7 flex md:items-end">
                            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                                A complete shipyard labor stack — welders,
                                fitters, machinists, scaffolders, and riggers.
                                Every crew member is qualified to Navy yard
                                procedures and ready to deploy.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10 border border-white/10">
                        {SERVICES.map((svc) => {
                            const Icon = SERVICE_ICONS[svc.slug] ?? Sparkles;
                            const span = SPAN_CLASSES[svc.slug] ?? "md:col-span-4";
                            const isHero = svc.slug === "navsea";
                            return (
                                <Link
                                    key={svc.slug}
                                    to="/services"
                                    data-testid={`service-card-${svc.slug}`}
                                    className={`group relative bg-brand-surface hover:bg-brand-surface2 transition-colors p-7 md:p-8 ${span} ${
                                        isHero
                                            ? "min-h-[360px] md:min-h-[440px]"
                                            : "min-h-[200px]"
                                    } flex flex-col justify-between overflow-hidden`}
                                >
                                    {isHero && (
                                        <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                                            <img
                                                src={IMAGES.pipeline}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-tr from-brand-surface via-brand-surface/70 to-transparent" />
                                        </div>
                                    )}
                                    <div className="relative">
                                        <div className="flex items-start justify-between mb-6">
                                            <Icon className="h-7 w-7 text-brand-blue" strokeWidth={1.5} />
                                            <span className="font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">
                                                / {String(SERVICES.indexOf(svc) + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                        <h3
                                            className={`font-display font-black text-white uppercase tracking-tight leading-none ${
                                                isHero
                                                    ? "text-3xl md:text-5xl"
                                                    : "text-xl md:text-2xl"
                                            }`}
                                        >
                                            {svc.title}
                                        </h3>
                                        <p
                                            className={`text-slate-400 mt-4 leading-relaxed ${
                                                isHero
                                                    ? "max-w-md text-base md:text-lg"
                                                    : "text-sm"
                                            }`}
                                        >
                                            {svc.short}
                                        </p>
                                    </div>
                                    <div className="relative flex items-center gap-2 mt-6 font-mono text-[11px] tracking-[0.2em] text-brand-blue uppercase">
                                        Learn More
                                        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PROOF BLOCK */}
            <section
                data-testid="home-proof"
                className="relative py-24 md:py-32 border-t border-white/10 bg-brand-surface/30"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
                        <div className="md:col-span-7">
                            <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                                ◇ 03 — Why LMJ
                            </div>
                            <h2 className="mt-4 font-display font-black text-white text-4xl md:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">
                                We don't bid jobs
                                <br />
                                we can't certify.
                            </h2>
                            <div className="mt-8 space-y-6 text-slate-300 max-w-2xl leading-relaxed">
                                <p>
                                    Every LMJ International welder, fitter and
                                    tradesperson is qualified to the procedure
                                    on file. We don't substitute experience for
                                    paperwork — we maintain both.
                                </p>
                                <p>
                                    When we commit to a scope, we commit the
                                    paperwork alongside it — procedures,
                                    quals, and documentation delivered with
                                    the crew. No surprises on the yard.
                                </p>
                            </div>

                            <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10 max-w-lg">
                                {[
                                    ["6+", "Years per journeyman"],
                                    ["100%", "Procedure-qualified"],
                                    ["NAVSEA", "On file"],
                                    ["48h", "Rapid response"],
                                ].map(([v, l]) => (
                                    <div
                                        key={l}
                                        className="bg-brand-surface px-5 py-6"
                                    >
                                        <div className="font-display font-black text-white text-3xl uppercase tracking-tight">
                                            {v}
                                        </div>
                                        <div className="font-mono text-[10px] tracking-[0.2em] text-slate-400 uppercase mt-1">
                                            {l}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="md:col-span-5 relative">
                            <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
                                <img
                                    src={IMAGES.pipe}
                                    alt="Steel pipe welding"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-transparent to-transparent" />
                                <div className="absolute bottom-6 left-6 right-6">
                                    <div className="font-mono text-[10px] tracking-[0.25em] text-brand-blue uppercase mb-2">
                                        Yard Procedure · CuNi
                                    </div>
                                    <div className="font-display font-bold text-white text-xl uppercase leading-tight">
                                        Pipe welding to Navy spec
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA BAND */}
            <section
                data-testid="home-cta"
                className="relative py-24 md:py-32 border-t border-white/10"
            >
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-8">
                            <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                                ◇ 04 — Mobilize
                            </div>
                            <h2 className="mt-4 font-display font-black text-white text-4xl md:text-6xl uppercase tracking-tighter leading-[0.95]">
                                Tell us about
                                <br />
                                your scope.
                            </h2>
                            <p className="mt-6 text-slate-300 max-w-xl text-lg leading-relaxed">
                                Drop us a note with your yard, procedure, and
                                timeline. We'll reply with crew availability
                                and a tailored quote.
                            </p>
                        </div>
                        <div className="md:col-span-4 flex md:justify-end">
                            <Link
                                to="/contact"
                                data-testid="home-cta-button"
                                className="group inline-flex items-center gap-3 bg-brand-blue hover:bg-[#004080] text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-8 py-5 transition-colors"
                            >
                                Start a Conversation
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
